import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Icon, TypeAhead } from '@gumtree/ui-library';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';

import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { pathHasLocationInUrl } from '@gumtree/shared/src/util/recent-location-pref-cookie';
import { locationFieldAriaLabel, locationFieldPlaceholderText } from '../constants';
import {
    trackClickSearchBar,
    setLocation,
    invalidateLocationRequests,
} from '../search-bar-actions';
import type { ShellUseDispatch } from '../../../reducers/common';
import { useClientGrowthbookFeatureValue } from '../../../growthbook/init-growthbook';
import { getCachedPreferredLocation } from '../recent-service/recent-locations-service';

interface LocationFieldProps {
    setLocationAndUpdateSuggestions(value: string): void;
    loadRecentLocationHistory(): void;
    userLoggedIn?: boolean;
    query: string;
    defaultLocation?: string;
    selectSuggestion(option: { name: string }, useRecentHistory: boolean): void;
    suggestions: any[];
    useRecentHistory: boolean;
    removeSingleFromHistory: (text: string) => void;
    clearAllFromHistory: () => void;
}

const LocationField: React.FC<LocationFieldProps> = ({
    setLocationAndUpdateSuggestions,
    loadRecentLocationHistory,
    userLoggedIn = false,
    query,
    defaultLocation = 'United Kingdom',
    selectSuggestion,
    suggestions = [],
    useRecentHistory,
    removeSingleFromHistory,
    clearAllFromHistory,
}) => {
    const dispatch = useDispatch() as ShellUseDispatch;
    const [hasTrackedLocationHistoryShown, setHasTrackedLocationHistoryShown] = useState(false);
    const [cachedLocation, setCachedLocation] = useState<string | null>(null);

    const debounceRef = useRef<number | undefined>(undefined);
    const DEBOUNCE_MS = 250;
    const isSrp4B = useClientGrowthbookFeatureValue(GrowthBookFeature.SRP_4, 'A') === 'B';
    const shouldLoadFromApi = isSrp4B && userLoggedIn;

    // Read cached preferred location synchronously before the browser paints
    // to prevent the "United Kingdom" → actual location flash on page load.
    useLayoutEffect(() => {
        const isSrpPage = /^\/search\b/.test(window.location.pathname);
        const pathHasLocation = pathHasLocationInUrl(window.location.pathname);
        if (query === defaultLocation && !isSrpPage && !pathHasLocation) {
            const cached = getCachedPreferredLocation();
            if (cached) {
                setCachedLocation(cached);
            }
        }
    }, []);

    // Show the cached value only while Redux still holds the default;
    // once loadRecentLocationHistory updates Redux, let it take over.
    const effectiveQuery = cachedLocation && query === defaultLocation ? cachedLocation : query;

    const handleChangeOptimised = useCallback(
        (value: string) => {
            dispatch(setLocation(value));
            setCachedLocation(null);
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = window.setTimeout(() => {
                setLocationAndUpdateSuggestions(value);
            }, DEBOUNCE_MS);
        },
        [setLocationAndUpdateSuggestions, dispatch]
    );

    useEffect(
        () => () => {
            dispatch(invalidateLocationRequests());
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        []
    );

    // Retry when srp4 B + login state become ready (avoids one-shot miss on fast hydrate).
    useEffect(() => {
        loadRecentLocationHistory();
    }, [loadRecentLocationHistory, shouldLoadFromApi]);

    // Keep the nav bar in sync when another location field (SRP, Cars) deletes an item.
    // removeSingleFromHistory / clearAllFromHistory broadcast 'recentLocationsChanged'
    // so we can reload without touching the TypeAhead open/close lifecycle.
    useEffect(() => {
        const handleRecentLocationsChanged = () => {
            loadRecentLocationHistory();
        };
        window.addEventListener('recentLocationsChanged', handleRecentLocationsChanged);
        return () => window.removeEventListener('recentLocationsChanged', handleRecentLocationsChanged);
    }, [loadRecentLocationHistory]);

    const inputProps = {
        'aria-label': locationFieldAriaLabel,
        placeholder: locationFieldPlaceholderText,
        ...qaAttribute('search-location-field'),
    };

    const handleFocus = useCallback(() => {
        window.setTimeout(() => {
            dispatch(trackClickSearchBar('location', suggestions.length));
        }, 0);
    }, [dispatch, suggestions.length]);

    const handleOpen = useCallback(() => {
        if (useRecentHistory && !hasTrackedLocationHistoryShown) {
            window.setTimeout(() => {
                trackV2('LocationHistoryShown');
                setHasTrackedLocationHistoryShown(true);
            }, 0);
        }
    }, [useRecentHistory, hasTrackedLocationHistoryShown]);

    return (
        <TypeAhead
            inputProps={inputProps}
            icon={<Icon type="beacon" size="medium" />}
            id="header-search-location"
            limit={10}
            name="search_location"
            onFocus={handleFocus}
            onOpen={handleOpen}
            onSelectOption={(option) => {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                selectSuggestion(option, useRecentHistory);
            }}
            onChange={handleChangeOptimised}
            options={suggestions}
            removeSingleFromHistory={removeSingleFromHistory}
            clearAllFromHistory={clearAllFromHistory}
            useRecentHistory={useRecentHistory}
            defaultValue={effectiveQuery}
            typeAheadIcon={
                useRecentHistory ? (
                    <Icon type="clock" size="medium" />
                ) : (
                    <Icon type="beacon" size="medium" />
                )
            }
            titleOfRecentHistory={`Recent Location${suggestions.length === 1 ? '' : 's'}`}
            showClearAllButton={false}
        />
    );
};

export default LocationField;
